import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/Button';
const WHATSAPP_NUMBER = '27732736906'; // Format: country code + number without leading 0
export function WhatsAppChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const handleSendMessage = () => {
    if (!message.trim()) return;
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    // Reset and close
    setMessage('');
    setIsOpen(false);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <>
      {/* Chat Widget */}
      <AnimatePresence data-id="element-1157">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-id="element-1158">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white" data-id="element-1159">
              <div className="flex items-center justify-between" data-id="element-1160">
                <div className="flex items-center gap-3" data-id="element-1161">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center" data-id="element-1162">
                    <MessageCircle className="w-6 h-6 text-green-600" data-id="element-1163" />
                  </div>
                  <div data-id="element-1164">
                    <h3 className="font-bold text-lg" data-id="element-1165">Bloom & Pop</h3>
                    <p className="text-xs text-green-100" data-id="element-1166">
                      Typically replies instantly
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-full transition-colors" data-id="element-1167">
                  <X className="w-5 h-5" data-id="element-1168" />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-4 bg-gray-50 min-h-[200px]" data-id="element-1169">
              {/* Welcome Message */}
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm mb-4" data-id="element-1170">
                <p className="text-sm text-gray-700" data-id="element-1171">
                  👋 Hi there! How can we help you today?
                </p>
                <p className="text-xs text-gray-500 mt-2" data-id="element-1172">
                  Send us a message and we'll get back to you on WhatsApp!
                </p>
              </motion.div>

              {/* Message Input */}
              <div className="space-y-3" data-id="element-1173">
                <textarea value={message} onChange={e => setMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message here..." className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 resize-none text-sm" rows={3} autoFocus data-id="element-1174" />

                <Button onClick={handleSendMessage} disabled={!message.trim()} className="w-full bg-green-600 hover:bg-green-700" rightIcon={<Send className="w-4 h-4" data-id="element-1176" />} data-id="element-1175">
                  Send on WhatsApp
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-100 border-t border-gray-200" data-id="element-1177">
              <p className="text-xs text-gray-500 text-center" data-id="element-1178">
                Powered by WhatsApp • We respect your privacy
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button onClick={() => setIsOpen(!isOpen)} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group" whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 1,
      duration: 0.3
    }} data-id="element-1179">
        <AnimatePresence mode="wait" data-id="element-1180">
          {isOpen ? <motion.div key="close" initial={{
          rotate: -90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: 90,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-1181">
              <X className="w-6 h-6" data-id="element-1182" />
            </motion.div> : <motion.div key="open" initial={{
          rotate: 90,
          opacity: 0
        }} animate={{
          rotate: 0,
          opacity: 1
        }} exit={{
          rotate: -90,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-1183">
              <MessageCircle className="w-6 h-6" data-id="element-1184" />
            </motion.div>}
        </AnimatePresence>

        {/* Notification Dot */}
        {!isOpen && <motion.span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" animate={{
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-1185" />}
      </motion.button>

      {/* Tooltip */}
      {!isOpen && <motion.div initial={{
      opacity: 0,
      x: 10
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 1.5
    }} className="fixed bottom-8 right-24 z-40 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg pointer-events-none" data-id="element-1186">
          Chat with us!
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-gray-900 rotate-45" data-id="element-1187" />
        </motion.div>}
    </>;
}